import { useSessionState } from '../../shared/hooks/session-state-hook';
import { useProfileState } from '../../shared/hooks/profile-state-hook';
import { sessionKey } from '../../shared/util/session-storage';
import PageHeading from '../../shared/components/UIElements/PageHeading';
import ProfileAvatar from '../../shared/components/UIElements/ProfileAvatar';
import Icon from '../../shared/components/UIElements/Icon';
import React, { useContext, useEffect, useState } from 'react';
import Card from '../../shared/components/UIElements/Card';
import { AuthContext } from '../../shared/Context/auth-context';
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner';
import { useHttpClient } from '../../shared/hooks/http-hook';
import Button from '../../shared/components/FormElements/Button';
import './PatientProfile.css';
import { BACKEND_URL } from '../../shared/util/backend-url';

const PatientProfile = () => {
  const auth = useContext(AuthContext);
  const { isLoading, sendRequest } = useHttpClient();

  const [editMode, setEditMode] = useSessionState(
    sessionKey(`patient:${auth.patientId}`, 'profile:editing'),
    false
  );
  const [patientData, setPatientData] = useProfileState(`patient:${auth.patientId}`, auth.patient, [
    'name',
    'email',
    'age',
    'gender',
    'mobilenumber',
    'height',
    'weight',
    'bloodGroup'
  ]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [careSummary, setCareSummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const patientId = auth.patientId || auth.patient?._id;

  // Initialize state with patient data from context
  useEffect(() => {
    if (auth.patient && !patientData) {
      setPatientData(auth.patient);
    }
  }, [auth.patient, patientData, setPatientData]);

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      setSummaryError('');
      try {
        const res = await sendRequest(
          `${BACKEND_URL}/api/care/patients/${patientId}/summary`,
          'GET',
          null,
          {
            Authorization: `Bearer ${auth.token}`
          }
        );

        if (cancelled) return;
        setPatientInfo(res.info || null);
        setCareSummary(res);
        // Keep an unfinished profile form intact while refreshing saved care details.
        if (res.patient && !editMode) setPatientData(res.patient);
      } catch (err) {
        if (!cancelled) setSummaryError('Could not refresh your health details. Please try again.');
      }
    };

    if (patientId && auth.token) fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [patientId, auth.token, sendRequest, setPatientData, editMode, refresh]);

  useEffect(() => {
    const refreshSummary = () => setRefresh((value) => value + 1);
    window.addEventListener('focus', refreshSummary);
    return () => window.removeEventListener('focus', refreshSummary);
  }, []);

  const prescriptionDate = careSummary?.latestPrescription?.date;
  const validPrescriptionDate = prescriptionDate && !Number.isNaN(Date.parse(prescriptionDate));
  const latestPrescriptionLabel = !careSummary
    ? 'Loading…'
    : !careSummary.latestPrescription
      ? 'No prescriptions yet'
      : validPrescriptionDate
        ? new Date(prescriptionDate).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })
        : 'Date not recorded';

  const inputChangedHandler = (event) => {
    const { name, value } = event.target;
    setPatientData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const submitHandler = async (event) => {
    event.preventDefault();
    try {
      const updatedData = await sendRequest(
        `${BACKEND_URL}/api/patient/${patientData._id}`,
        'PATCH',
        JSON.stringify({
          name: patientData.name,
          email: patientData.email,
          age: patientData.age,
          gender: patientData.gender,
          mobilenumber: patientData.mobilenumber,
          height: patientData.height,
          weight: patientData.weight,
          bloodGroup: patientData.bloodGroup
        }),
        {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`
        }
      );
      auth.Plogin(updatedData.updatedPatient || updatedData.patient, auth.token);
      setPatientData(updatedData.updatedPatient || updatedData.patient);
      setEditMode(false);
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  if (!patientData) {
    return (
      <div className="patient-profile">
        <Card className="patient-card">
          <LoadingSpinner asOverlay />
        </Card>
      </div>
    );
  }

  return (
    <div className="patient-profile">
      <PageHeading
        eyebrow="YOUR HEALTH, TOGETHER"
        title={`Hello, ${patientData.name}.`}
        description="Your health details and care journey, all in one place."
      >
        <Button inverse to={`/${auth.patientId}/history`}>
          <Icon name="history" size={18} /> Medical history
        </Button>
        <Button to={`/reports/${auth.patientId}`}>
          <Icon name="file" size={18} /> Health reports
        </Button>
      </PageHeading>
      {summaryError && (
        <div role="alert" className="action-row">
          <p>{summaryError}</p>
          <Button inverse onClick={() => setRefresh((value) => value + 1)}>
            Retry
          </Button>
        </div>
      )}
      <div className="patient-metrics">
        {[
          ['pulse', 'Height', patientData.height],
          ['heart', 'Weight', patientData.weight],
          ['patient', 'Blood group', patientData.bloodGroup]
        ].map(([icon, label, value]) => (
          <div className="health-metric" key={label}>
            <span className="summary-icon">
              <Icon name={icon} size={23} />
            </span>
            <div>
              <p>{label}</p>
              <strong>{value || 'Not provided'}</strong>
            </div>
          </div>
        ))}
      </div>
      <div className="patient-layout">
        <Card className="patient-card patient-card--profile">
          {isLoading && <LoadingSpinner asOverlay />}
          <div className="panel-heading">
            <h2>Personal details</h2>
            <span className="badge">My profile</span>
          </div>
          <div className="profile-identity">
            <ProfileAvatar
              name={patientData.name}
              src={
                patientData.profileImage
                  ? `${BACKEND_URL}/${patientData.profileImage}`
                  : undefined
              }
              className="profile-avatar--large"
            />
            <div>
              <h2>{patientData.name}</h2>
              <p>
                {patientData.age} years · {patientData.gender}
              </p>
            </div>
          </div>
          {!editMode ? (
            <>
              <dl className="detail-list">
                <div>
                  <dt>Email address</dt>
                  <dd>{patientData.email}</dd>
                </div>
                <div>
                  <dt>Contact number</dt>
                  <dd>{patientData.mobilenumber}</dd>
                </div>
                <div>
                  <dt>Patient ID</dt>
                  <dd>{patientData._id}</dd>
                </div>
              </dl>
              <Button inverse onClick={() => setEditMode(true)}>
                Edit profile
              </Button>
            </>
          ) : (
            <form className="profile-form" onSubmit={submitHandler}>
              <button
                type="button"
                className="modal-back full-width"
                onClick={() => setEditMode(false)}
              >
                <Icon name="back" size={17} />
                Back to profile
              </button>
              {[
                ['name', 'Name'],
                ['email', 'Email address'],
                ['age', 'Age'],
                ['gender', 'Gender'],
                ['mobilenumber', 'Contact number']
              ].map(([name, label]) => (
                <div key={name}>
                  <label htmlFor={`patient-${name}`}>{label}</label>
                  <input
                    id={`patient-${name}`}
                    type={name === 'email' ? 'email' : name === 'age' ? 'number' : 'text'}
                    name={name}
                    value={patientData[name] || ''}
                    onChange={inputChangedHandler}
                  />
                </div>
              ))}
              <div className="action-row">
                <Button type="submit">Save changes</Button>
                <Button inverse type="button" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </Card>
        <div className="patient-health-column">
          <Card className="patient-card">
            <div className="panel-heading">
              <h2>Vitals & latest care</h2>
              <Icon name="pulse" />
            </div>
            {!editMode ? (
              <dl className="detail-list detail-list--grid">
                <div>
                  <dt>Height</dt>
                  <dd>{patientData.height || 'Not provided'}</dd>
                </div>
                <div>
                  <dt>Weight</dt>
                  <dd>{patientData.weight || 'Not provided'}</dd>
                </div>
                <div>
                  <dt>Blood group</dt>
                  <dd>{patientData.bloodGroup || 'Not provided'}</dd>
                </div>
                <div>
                  <dt>Last prescription</dt>
                  <dd>
                    {summaryError && !careSummary ? 'Unable to load' : latestPrescriptionLabel}
                  </dd>
                </div>
                <div>
                  <dt>Primary doctor</dt>
                  <dd>
                    {careSummary?.primaryDoctor?.name ||
                      (careSummary
                        ? 'No primary doctor selected'
                        : summaryError
                          ? 'Unable to load'
                          : 'Loading…')}
                  </dd>
                </div>
                {careSummary?.primaryDoctor && (
                  <div>
                    <dt>Specialty</dt>
                    <dd>{careSummary.primaryDoctor.specialization || 'Not provided'}</dd>
                  </div>
                )}
              </dl>
            ) : (
              <div className="profile-form">
                {[
                  ['height', 'Height'],
                  ['weight', 'Weight'],
                  ['bloodGroup', 'Blood group']
                ].map(([name, label]) => (
                  <div key={name}>
                    <label htmlFor={`patient-${name}`}>{label}</label>
                    <input
                      id={`patient-${name}`}
                      type="text"
                      name={name}
                      value={patientData[name] || ''}
                      onChange={inputChangedHandler}
                    />
                  </div>
                ))}
                <p className="muted full-width">Save your profile to update these details.</p>
              </div>
            )}
          </Card>
          <Card className="patient-card">
            <div className="panel-heading">
              <h2>Medical information</h2>
              <Icon name="heart" />
            </div>
            <dl className="detail-list detail-list--grid">
              <div>
                <dt>Comorbidities</dt>
                <dd>{patientInfo?.comorbidities || 'Not provided'}</dd>
              </div>
              <div>
                <dt>Allergies</dt>
                <dd>{patientInfo?.allergies || 'Not provided'}</dd>
              </div>
              <div>
                <dt>Past allergies</dt>
                <dd>{patientInfo?.pastAllergies || 'Not provided'}</dd>
              </div>
              <div>
                <dt>Identification marks</dt>
                <dd>{patientInfo?.identificationMarks || 'Not provided'}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
};
export default PatientProfile;
